import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores';
import { useMutation } from '@tanstack/react-query';

export type Flow =
  | 'confirm_primary_customer_info'
  | 'confirm_primary_customer_delivery_address'
  | 'confirm_co_registrant_info';

export type FlowStep =
  | 'init'
  | 'sending_initial'
  | 'awaiting_reply'
  | 'processing_reply'
  | 'completed'
  | 'error';

const useSmsFlowStepMutation = ({
  dealId,
  shortId,
  onSuccess,
  onError,
}: {
  dealId: Deal['id'] | null | undefined;
  shortId: Deal['short_id'] | null | undefined;
  onSuccess?: (
    data: Deal,
    variables: { flow?: Flow; flowStep?: FlowStep },
  ) => void;
  onError?: (
    error: Error,
    variables: { flow?: Flow; flowStep?: FlowStep },
  ) => void;
}) => {
  const smsFlowStepMutation = useMutation({
    onMutate: async ({ flow, flowStep }) => {
      if (!dealId || !shortId) {
        throw new Error('Deal ID or short ID is required');
      }

      // Optimistically update the local store
      const deal = dealStore.getById(dealId);
      if (deal) {
        if (flow !== undefined) {
          deal.sms_flow = flow;
        }
        if (flowStep !== undefined) {
          deal.sms_flow_step = flowStep;
        }
        dealStore.save(deal);
      }
    },
    mutationFn: async ({
      flow,
      flowStep,
    }: {
      flow?: Flow;
      flowStep?: FlowStep;
    }) => {
      const payload: { sms_flow?: Flow; sms_flow_step?: FlowStep } = {};
      if (flow !== undefined) {
        payload.sms_flow = flow;
      }
      if (flowStep !== undefined) {
        payload.sms_flow_step = flowStep;
      }

      const response = await pdkAxios.post(
        `/deals/${dealId}/sms-flow`,
        payload,
      );

      return response.data;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);

      console.error('Error updating SMS flow step', error, variables);
    },
    onSettled: () => {},
  });

  return { smsFlowStepMutation };
};

export default useSmsFlowStepMutation;
